import { db } from '../firebase';
import {
    collection,
    addDoc,
    getDocs,
    query,
    where,
    orderBy,
    doc,
    setDoc,
    getDoc,
    updateDoc,
    serverTimestamp,
    writeBatch
} from 'firebase/firestore';
import {
    COLLECTION_DEPARTMENTS,
    COLLECTION_SPECIALTIES
} from '../shared/constants';

/**
 * Get only ACTIVE departments ordered by 'order' field.
 * Used in referral creation dropdowns — inactive departments are hidden.
 */
export async function getAllDepartments() {
    try {
        const q = query(
            collection(db, COLLECTION_DEPARTMENTS),
            orderBy('order', 'asc')
        );
        const snapshot = await getDocs(q);
        return snapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(d => d.active !== false);
    } catch (error) {
        console.error('Error getting departments:', error);
        throw error;
    }
}

/**
 * Get ALL departments (including inactive) — for the Taxonomy Management admin page.
 */
export async function getAllDepartmentsAdmin() {
    try {
        const q = query(
            collection(db, COLLECTION_DEPARTMENTS),
            orderBy('order', 'asc')
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error('Error getting departments (admin):', error);
        throw error;
    }
}

/**
 * Get only ACTIVE specialties for a specific department.
 * Used in referral creation dropdowns — inactive specialties are hidden.
 * @param {string} departmentId
 */
export async function getSpecialtiesByDepartment(departmentId) {
    if (!departmentId) return [];
    try {
        const q = query(
            collection(db, COLLECTION_SPECIALTIES),
            where('departmentId', '==', departmentId)
        );
        const snapshot = await getDocs(q);
        const specialties = snapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(s => s.active !== false);
        return specialties.sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
        console.error('Error getting specialties:', error);
        throw error;
    }
}

/**
 * Get ALL specialties for a department (including inactive) — for Taxonomy Management.
 * @param {string} departmentId
 */
export async function getAllSpecialtiesByDepartmentAdmin(departmentId) {
    if (!departmentId) return [];
    try {
        const q = query(
            collection(db, COLLECTION_SPECIALTIES),
            where('departmentId', '==', departmentId)
        );
        const snapshot = await getDocs(q);
        const specialties = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        return specialties.sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
        console.error('Error getting specialties (admin):', error);
        throw error;
    }
}

/**
 * Helper to generate slug IDs
 */
function generateSlug(text) {
    return text
        .toLowerCase()
        .replace(/[&]/g, 'and')
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '_')
        .trim();
}

/**
 * Add a new department with slug ID
 */
export async function addDepartment(deptData) {
    try {
        const slug = generateSlug(deptData.name);
        const docRef = doc(db, COLLECTION_DEPARTMENTS, slug);

        await setDoc(docRef, {
            ...deptData,
            active: true,
            createdAt: serverTimestamp()
        });

        return { id: slug, ...deptData };
    } catch (error) {
        console.error('Error adding department:', error);
        throw error;
    }
}

/**
 * Add a new specialty with slug ID
 */
export async function addSpecialty(specData) {
    try {
        const slug = generateSlug(specData.name);
        const docRef = doc(db, COLLECTION_SPECIALTIES, slug);

        await setDoc(docRef, {
            ...specData,
            active: true,
            createdAt: serverTimestamp()
        });

        return { id: slug, ...specData };
    } catch (error) {
        console.error('Error adding specialty:', error);
        throw error;
    }
}

/**
 * Toggle department active status
 */
export async function toggleDepartmentActive(departmentId, currentStatus) {
    try {
        const docRef = doc(db, COLLECTION_DEPARTMENTS, departmentId);
        await updateDoc(docRef, {
            active: !currentStatus,
            updatedAt: serverTimestamp()
        });
        return !currentStatus;
    } catch (error) {
        console.error('Error toggling department status:', error);
        throw error;
    }
}

/**
 * Toggle specialty active status
 */
export async function toggleSpecialtyActive(specialtyId, currentStatus) {
    try {
        const docRef = doc(db, COLLECTION_SPECIALTIES, specialtyId);
        await updateDoc(docRef, {
            active: !currentStatus,
            updatedAt: serverTimestamp()
        });
        return !currentStatus;
    } catch (error) {
        console.error('Error toggling specialty status:', error);
        throw error;
    }
}

/**
 * Update department display order
 */
export async function updateDepartmentOrder(departmentId, newOrder) {
    try {
        const docRef = doc(db, COLLECTION_DEPARTMENTS, departmentId);
        await updateDoc(docRef, {
            order: newOrder,
            updatedAt: serverTimestamp()
        });
    } catch (error) {
        console.error('Error updating department order:', error);
        throw error;
    }
}

/**
 * Seed initial taxonomy data
 * Performs a full wipe of existing departments and specialties before seeding
 */
export async function seedTaxonomy() {
    const taxonomy = [
        {
            name: "Allopathy – General Medicine & Related Specialties",
            order: 1,
            specialties: [
                "General Medicine", "Cardiology", "Endocrinology", "Gastroenterology",
                "Nephrology", "Pulmonology", "Rheumatology"
            ]
        },
        {
            name: "Allopathy – Surgical Specialties",
            order: 2,
            specialties: [
                "General Surgery", "Cardiothoracic Surgery", "Neurosurgery",
                "Orthopedic Surgery", "Urology", "Plastic & Reconstructive Surgery",
                "Pediatric Surgery", "Vascular Surgery"
            ]
        },
        {
            name: "Women’s Health",
            order: 3,
            specialties: [
                "Obstetrics & Gynecology (OB-GYN)", "Maternal-Fetal Medicine", "Gynecologic Oncology"
            ]
        },
        {
            name: "Pediatrics & Neonatology",
            order: 4,
            specialties: [
                "Pediatrics", "Pediatric Neurology", "Pediatric Cardiology",
                "Pediatric Endocrinology", "Neonatology"
            ]
        },
        {
            name: "Oncology",
            order: 5,
            specialties: [
                "Medical Oncology", "Surgical Oncology", "Radiation Oncology"
            ]
        },
        {
            name: "Neurology & Psychiatry",
            order: 6,
            specialties: [
                "Neurology", "Psychiatry", "Child Psychiatry", "Addiction Psychiatry"
            ]
        },
        {
            name: "Critical Care & Emergency Medicine",
            order: 7,
            specialties: [
                "Emergency Medicine", "Critical Care Medicine"
            ]
        },
        {
            name: "Diagnostics & Lab-Based Specialties",
            order: 8,
            specialties: [
                "Radiology", "Interventional Radiology", "Pathology",
                "Histopathology", "Hematology"
            ]
        },
        {
            name: "Other Specialized Fields",
            order: 9,
            specialties: [
                "Dermatology", "Ophthalmology", "Otorhinolaryngology (ENT)",
                "Anesthesiology", "Physical Medicine & Rehabilitation", "Geriatrics"
            ]
        },
        {
            name: "Dentistry",
            order: 10,
            specialties: [
                "General Dentistry", "Orthodontics", "Periodontics", "Endodontics",
                "Oral & Maxillofacial Surgery", "Prosthodontics", "Pedodontics",
                "Oral Pathology", "Oral Medicine & Radiology", "Public Health Dentistry"
            ]
        },
        {
            name: "Medical Practitioners (AYUSH & Others)",
            order: 11,
            specialties: [
                "Allopathic Doctors", "Ayurvedic Doctors", "Homeopathic Doctors",
                "Unani Practitioners", "Siddha Practitioners", "Naturopaths & Yoga Therapists"
            ]
        },
        {
            name: "Nurses & Allied Health Professionals",
            order: 12,
            specialties: [
                "Nurses", "Physiotherapists", "Occupational Therapists",
                "Speech-Language Pathologists", "Radiographers & Imaging Technicians",
                "Lab Technicians"
            ]
        },
        {
            name: "Pharmacological Experts",
            order: 13,
            specialties: [
                "Pharmacists", "Pharmacologists", "Toxicologists"
            ]
        },
        {
            name: "Mental Health Professionals",
            order: 14,
            specialties: [
                "Psychologists", "Psychiatrists", "Counselors & Therapists"
            ]
        },
        {
            name: "Diet & Nutrition Experts",
            order: 15,
            specialties: [
                "Dietitians", "Nutritionists", "Sports Nutritionists"
            ]
        },
        {
            name: "Traditional & Complementary Medicine",
            order: 16,
            specialties: [
                "Chiropractors", "Acupuncturists", "Reiki Healers",
                "Traditional Chinese Medicine Practitioners"
            ]
        },
        {
            name: "Community & Preventive Health",
            order: 17,
            specialties: [
                "Public Health Specialists", "Epidemiologists"
            ]
        },
        {
            name: "Dental & Oral Health Experts",
            order: 18,
            specialties: [
                "Dentists", "Dental Hygienists"
            ]
        },
        {
            name: "Vision & Hearing Specialists",
            order: 19,
            specialties: [
                "Optometrists", "Audiologists"
            ]
        },
        {
            name: "Emergency & Critical Care Specialists",
            order: 20,
            specialties: [
                "Paramedics", "Emergency Medicine Physicians"
            ]
        },
        {
            name: "Holistic Health & Wellness Experts",
            order: 21,
            specialties: [
                "Wellness Coaches", "Fitness Trainers"
            ]
        },
        {
            name: "Veterinary Professionals",
            order: 22,
            specialties: [
                "Veterinarians"
            ]
        },
        {
            name: "Healthcare Support Roles",
            order: 23,
            specialties: [
                "Medical Social Workers", "Healthcare Administrators"
            ]
        }
    ];

    try {
        console.log("Starting Medical Categories Refresh...");

        // 1. Fetch all existing records to delete
        const deptsSnap = await getDocs(collection(db, COLLECTION_DEPARTMENTS));
        const specsSnap = await getDocs(collection(db, COLLECTION_SPECIALTIES));

        const deleteBatch = writeBatch(db);
        deptsSnap.forEach(d => deleteBatch.delete(d.ref));
        specsSnap.forEach(s => deleteBatch.delete(s.ref));

        console.log(`Wiping ${deptsSnap.size} departments and ${specsSnap.size} specialties...`);
        await deleteBatch.commit();

        // 2. Seed new hierarchy
        const seedBatch = writeBatch(db);

        for (const dept of taxonomy) {
            const deptId = generateSlug(dept.name);
            const deptRef = doc(db, COLLECTION_DEPARTMENTS, deptId);

            seedBatch.set(deptRef, {
                name: dept.name,
                order: dept.order,
                active: true,
                createdAt: serverTimestamp()
            });

            console.log(`Seeding Department: ${dept.name}`);

            for (const specName of dept.specialties) {
                const specId = generateSlug(specName);
                const specRef = doc(db, COLLECTION_SPECIALTIES, specId);

                seedBatch.set(specRef, {
                    name: specName,
                    departmentId: deptId,
                    active: true,
                    createdAt: serverTimestamp()
                });
            }
        }

        await seedBatch.commit();
        console.log("Successfully refreshed all medical categories!");
        return { success: true };
    } catch (error) {
        console.error("Refresh Failed:", error);
        throw error;
    }
}
